import React from "react";
import { Box, TextField, Button, Typography, Alert, Paper, Stack } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  setForm,
  setOtpSent,
  setUserId,
  setOtp,
  setMsg,
  setSuccess,
  resetSignup,
} from "../store/signupSlice";
import "./Auth.css";

export default function Signup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { form, otpSent, userId, otp, msg, success } = useSelector(
    (state) => state.signup
  );

  // Add local state for confirm password
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Debug logging
  React.useEffect(() => {
    console.log("Signup component state:", { form, otpSent, userId, otp, msg, success });
  }, [form, otpSent, userId, otp, msg, success]);

  const handleChange = (e) => {
    console.log("Form field changed:", e.target.name, "=", e.target.value);
    dispatch(setForm({ [e.target.name]: e.target.value }));
  };

  const handleSignup = async (e) => {
    console.log("handleSignup called", { form, confirmPassword });
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) {
      console.log("Already submitting, ignoring duplicate request");
      return;
    }
    
    setIsSubmitting(true);
    dispatch(setMsg(""));
    dispatch(setSuccess(false));
    
    // Validate form data
    if (!form.username || !form.email || !form.password) {
      console.log("Missing form fields", form);
      dispatch(setMsg("Please fill all required fields"));
      setIsSubmitting(false);
      return;
    }
    
    if (form.password !== confirmPassword) {
      console.log("Password mismatch");
      dispatch(setMsg("Passwords do not match"));
      setIsSubmitting(false);
      return;
    }
    
    console.log("Making API request to signup");
    console.log("Request payload:", JSON.stringify(form, null, 2));
    
    try {
      const res = await fetch("https://razchat-backend-1.onrender.com/api/auth/signup", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(form),
      });
      
      console.log("Response received - Status:", res.status, "OK:", res.ok);
      
      const data = await res.json();
      console.log("API response", { 
        status: res.status, 
        statusText: res.statusText, 
        data,
        ok: res.ok 
      });
      
      if (res.ok) {
        dispatch(setOtpSent(true));
        dispatch(setUserId(data.userId));
        dispatch(setMsg("OTP sent to your email."));
        dispatch(setSuccess(true));
        console.log("Signup successful, OTP sent");
      } else {
        // Handle different types of errors
        let errorMessage = data.error || data.message || `Signup failed (${res.status})`;
        
        // Provide user-friendly messages for common backend issues
        if (res.status === 500) {
          if (errorMessage.includes('nodemailer') || errorMessage.includes('OTP email') || errorMessage.includes('SMTP') || errorMessage.includes('authentication failed') || errorMessage.includes('Invalid login')) {
            errorMessage = "Email service configuration error. Please contact support or try again later.";
            console.warn("Backend email service configuration issue. Check SMTP settings, authentication credentials, or app passwords.");
          } else {
            errorMessage = "Server error occurred. Please try again later.";
          }
        } else if (res.status === 400) {
          if (errorMessage.includes('already registered') || errorMessage.includes('exists')) {
            errorMessage = "This email is already registered. Please use a different email or try logging in.";
          }
        }
        
        console.log("Signup failed:", errorMessage);
        dispatch(setMsg(errorMessage));
        dispatch(setSuccess(false));
      }
    } catch (error) {
      console.error("Signup error details:", {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        dispatch(setMsg("Cannot connect to server. Please check your internet connection."));
      } else {
        dispatch(setMsg("Network error. Please try again."));
      }
      dispatch(setSuccess(false));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    dispatch(setMsg(""));
    dispatch(setSuccess(false));
    const res = await fetch("https://razchat-backend-1.onrender.com/api/auth/verify-otp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, otp }),
    });
    const data = await res.json();
    if (res.ok) {
      dispatch(setMsg("Account verified! Please login."));
      dispatch(setSuccess(true));
      dispatch(resetSignup());
      navigate("/login");
    } else {
      dispatch(setMsg(data.error));
    }
  };

  return (
    <div className="auth-bg">
      <Paper elevation={3} className="auth-paper">
        <Typography variant="h4" className="auth-title" align="center" mb={2}>
          Sign Up
        </Typography>
        {!otpSent ? (
          <Box component="form" onSubmit={handleSignup}>
            <Stack spacing={2}>
              <TextField
                name="username"
                label="Username"
                value={form.username}
                onChange={handleChange}
                required
                fullWidth
                className="auth-input"
              />
              <TextField
                name="email"
                label="Email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                fullWidth
                className="auth-input"
              />
              <TextField
                name="password"
                label="Password"
                type="password"
                value={form.password}
                onChange={handleChange}
                required
                fullWidth
                className="auth-input"
              />
              <TextField
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                fullWidth
                className="auth-input"
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                className="auth-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating Account..." : "Sign Up"}
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleVerify}>
            <Stack spacing={2}>
              <TextField
                label="Enter OTP"
                value={otp}
                onChange={(e) => dispatch(setOtp(e.target.value || ""))}
                required
                fullWidth
                className="auth-input"
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                className="auth-btn"
              >
                Verify OTP
              </Button>
            </Stack>
          </Box>
        )}
        {msg && (
          <Alert 
            severity={success ? "success" : "error"} 
            sx={{ 
              mt: 2,
              fontSize: '0.95rem',
              '& .MuiAlert-message': {
                fontWeight: 600
              }
            }}
          >
            {msg}
          </Alert>
        )}
        <Typography mt={2} align="center">
          Already have an account? <Link to="/login" className="auth-link">Login</Link>
        </Typography>
      </Paper>
    </div>
  );
}
